import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
    Box, Typography, Button, Breadcrumbs, Link, CircularProgress, Accordion,
    AccordionSummary, AccordionDetails, IconButton, List, ListItem, ListItemText,
    ListItemIcon, Select, MenuItem, FormControl, InputLabel, Tooltip, Paper,
    Avatar, Chip, Fade, TextField
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArticleIcon from '@mui/icons-material/Article';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import QuizIcon from '@mui/icons-material/Quiz';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SchoolIcon from '@mui/icons-material/School';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../services/api';
import { Course, Section, Lesson, VideoContent, QuizContent } from '../types/course';
import { Block } from '../components/course/BlockEditor';
import SectionModal from '../components/course/modals/SectionModal';
import ContentEditor from '../components/course/ContentEditor';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { designTokens } from '../theme/designTokens';

const CourseDetails = () => {
    const { courseId } = useParams<{ courseId: string }>();
    const [course, setCourse] = useState<Course | null>(null);
    const [loading, setLoading] = useState(true);

    // Modals
    const [sectionModalOpen, setSectionModalOpen] = useState(false);
    const [editingSection, setEditingSection] = useState<Section | null>(null);

    const [contentEditorOpen, setContentEditorOpen] = useState(false);
    const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
    const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
    const [newLessonType, setNewLessonType] = useState<'text' | 'video' | 'quiz' | 'exercise' | null>(null);
    const [lessonTitle, setLessonTitle] = useState('');

    // Categories for selector
    const [categories, setCategories] = useState<{ _id: string; title: string }[]>([]);
    const [categoryLoading, setCategoryLoading] = useState(false);

    useEffect(() => {
        fetchCourseDetails();
        fetchCategories();
    }, [courseId]);

    const fetchCategories = async () => {
        try {
            const res = await api.get('/api/course/categories/all');
            setCategories(res.data);
        } catch (err) {
            console.error("Error fetching categories", err);
        }
    };

    const fetchCourseDetails = async () => {
        try {
            const res = await api.get(`/api/course/course/${courseId}`);
            setCourse(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching course details", err);
            setLoading(false);
        }
    };

    const handleCategoryChange = async (newCategoryId: string) => {
        if (!course) return;
        setCategoryLoading(true);
        try {
            await api.put(`/api/course/course/${courseId}`, { categoryId: newCategoryId || null });
            setCourse({ ...course, categoryId: newCategoryId || undefined });
        } catch (err) {
            console.error("Error updating category", err);
        } finally {
            setCategoryLoading(false);
        }
    };

    // Section Handlers
    const handleOpenSectionModal = (section: Section | null = null) => {
        setEditingSection(section);
        setSectionModalOpen(true);
    };

    const handleSaveSection = async (data: any) => {
        try {
            if (editingSection) {
                await api.put(`/api/course/section/${editingSection._id}`, data);
            } else {
                await api.post('/api/course/section', {
                    ...data,
                    courseId: courseId
                });
            }
            fetchCourseDetails();
        } catch (err) {
            console.error("Error saving section", err);
        }
    };

    // Lesson Handlers
    const handleAddLesson = (sectionId: string, type: 'text' | 'video' | 'quiz' | 'exercise') => {
        setActiveSectionId(sectionId);
        setNewLessonType(type);
        setEditingLesson(null); // New lesson
        setLessonTitle(`Nouvelle leçon ${type}`);
        setContentEditorOpen(true);
    };

    const handleEditLesson = (sectionId: string, lesson: Lesson) => {
        setActiveSectionId(sectionId);
        setEditingLesson(lesson);
        setLessonTitle(lesson.title);
        setContentEditorOpen(true);
    };

    const handleDeleteLesson = async (lessonId: string) => {
        if (!window.confirm("Voulez-vous vraiment supprimer cette leçon ?")) return;
        try {
            await api.delete(`/api/lesson/${lessonId}`);
            fetchCourseDetails();
        } catch (err) {
            console.error("Error deleting lesson", err);
        }
    };

    const handleSaveContent = async (
        _ignoreTitle: string,
        content: string,
        video: VideoContent | null,
        quiz: QuizContent | null,
        type: 'text' | 'video' | 'quiz' | 'exercise',
        blocks?: Block[],
        exercise?: { statementBlocks: Block[]; correctionBlocks: Block[]; aiCorrectionEnabled: boolean }
    ) => {
        const title = lessonTitle;
        if (!activeSectionId) return;

        try {
            if (editingLesson) {
                // Update existing lesson
                await api.put(`/api/lesson/${editingLesson._id}`, {
                    title: title || editingLesson.title,
                    type,
                    blocks,
                    video,
                    quiz,
                    exercise
                });
            } else {
                // Create new lesson
                await api.post('/api/lesson', {
                    title: title || `Nouvelle leçon ${type}`,
                    type,
                    sectionId: activeSectionId,
                    blocks,
                    video,
                    quiz,
                    exercise
                });
            }
            setContentEditorOpen(false);
            fetchCourseDetails();
        } catch (err) {
            console.error("Error saving lesson", err);
        }
    };

    const getIconForType = (type: string) => {
        switch (type) {
            case 'video': return <PlayCircleIcon sx={{ color: '#764ba2' }} />;
            case 'quiz': return <QuizIcon sx={{ color: '#10b981' }} />;
            case 'exercise': return <SchoolIcon sx={{ color: '#ff8c00' }} />;
            default: return <ArticleIcon sx={{ color: '#667eea' }} />;
        }
    };

    // Drag & Drop Handlers
    const moveSection = (dragIndex: number, hoverIndex: number) => {
        if (!course || !course.sections) return;
        const dragSection = course.sections[dragIndex];
        const newSections = [...course.sections];
        newSections.splice(dragIndex, 1);
        newSections.splice(hoverIndex, 0, dragSection);

        // Update local state immediately for smooth UI
        setCourse({ ...course, sections: newSections });
    };

    const handleDragEnd = async () => {
        if (!course || !course.sections) return;

        // Prepare payload with new order
        const updates = course.sections.map((section, index) => ({
            id: section._id,
            order: index
        }));

        try {
            await Promise.all(updates.map(u => api.put(`/api/course/section/${u.id}`, { order: u.order })));
        } catch (err) {
            console.error("Error updating section order", err);
            fetchCourseDetails(); // Revert on error
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10, minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)' }}><CircularProgress /></Box>;
    if (!course) return <Typography>Cours introuvable</Typography>;

    return (
        <DndProvider backend={HTML5Backend}>
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
                p: { xs: 2, md: 4 }
            }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                    <Link
                        component={RouterLink}
                        underline="hover"
                        color="inherit"
                        to="/courses"
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} /> Liste des Cours
                    </Link>
                    <Typography color="text.primary" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                        {course.title}
                    </Typography>
                </Breadcrumbs>

                {/* Hero Header */}
                <Paper
                    elevation={0}
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 4,
                        p: { xs: 3, md: 5 },
                        mb: 5,
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 4,
                        alignItems: 'center'
                    }}
                >
                    {/* Decorative circles */}
                    <Box sx={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 250,
                        height: 250,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)',
                    }} />
                    <Box sx={{
                        position: 'absolute',
                        bottom: -100,
                        left: -50,
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.05)',
                    }} />

                    {course.imageUrl ? (
                        <Box
                            component="img"
                            src={course.imageUrl}
                            alt={course.title}
                            sx={{
                                width: { xs: '100%', md: 240 },
                                height: 160,
                                objectFit: 'cover',
                                borderRadius: 4,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                                zIndex: 1
                            }}
                        />
                    ) : (
                        <Avatar
                            sx={{
                                width: 120,
                                height: 120,
                                bgcolor: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: 4
                            }}
                        >
                            <SchoolIcon sx={{ fontSize: 60 }} />
                        </Avatar>
                    )}

                    <Box sx={{ flexGrow: 1, zIndex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, alignItems: 'center', gap: 2, mb: 1 }}>
                                <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                                    {course.title}
                                </Typography>
                                <Chip
                                    label={course.status === 'publish' ? 'Publié' : 'Brouillon'}
                                    sx={{
                                        bgcolor: course.status === 'publish' ? '#10b981' : 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 700,
                                        fontFamily: 'Quicksand, sans-serif'
                                    }}
                                />
                            </Box>
                            <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 600, mb: 2 }}>
                                {course.description || "Aucune description fournie pour ce cours."}
                            </Typography>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' }, gap: 3, alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <FormControl size="small" sx={{
                                        minWidth: 200,
                                        '& .MuiOutlinedInput-root': {
                                            color: 'white',
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            borderRadius: 2,
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: 'white' },
                                        },
                                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                        '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                                        '& .MuiSelect-icon': { color: 'white' }
                                    }}>
                                        <InputLabel id="category-select-label">Catégorie</InputLabel>
                                        <Select
                                            labelId="category-select-label"
                                            value={course.categoryId || ''}
                                            label="Catégorie"
                                            onChange={(e) => handleCategoryChange(e.target.value as string)}
                                            disabled={categoryLoading}
                                        >
                                            <MenuItem value=""><em>Aucune catégorie</em></MenuItem>
                                            {categories.map((cat) => (
                                                <MenuItem key={cat._id} value={cat._id}>{cat.title}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {categoryLoading && <CircularProgress size={20} sx={{ color: 'white' }} />}
                                </Box>
                                <Box sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                                    Créé le: {course.createdAt ? new Date(course.createdAt).toLocaleDateString() : 'N/A'}
                                </Box>
                                <Button
                                    variant="contained"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => window.open(`/learner/course/${course._id}`, '_blank')}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        backdropFilter: 'blur(10px)',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        fontFamily: 'Quicksand, sans-serif',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.3)'
                                        }
                                    }}
                                >
                                    Pré-visualiser
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#667eea', width: 40, height: 40 }}>
                            <PlayCircleOutlineIcon />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif' }}>
                            Structure du Cours
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenSectionModal(null)}
                        sx={{
                            ...designTokens.actionButton,
                            px: 3,
                            py: 1.2,
                            fontFamily: 'Quicksand, sans-serif',
                            fontWeight: 700
                        }}
                    >
                        Ajouter une Section
                    </Button>
                </Box>

                <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
                    {course.sections?.map((section, index) => (
                        <Fade in key={section._id} timeout={300 + index * 100}>
                            <Box>
                                <DraggableSection
                                    section={section}
                                    index={index}
                                    moveSection={moveSection}
                                    onDragEnd={handleDragEnd}
                                    onEdit={() => handleOpenSectionModal(section)}
                                    onEditLesson={handleEditLesson}
                                    onDeleteLesson={handleDeleteLesson}
                                    onAddLesson={handleAddLesson}
                                    getIconForType={getIconForType}
                                />
                            </Box>
                        </Fade>
                    ))}
                    {(!course.sections || course.sections.length === 0) && (
                        <Paper sx={{
                            ...designTokens.glassCard,
                            p: 8,
                            textAlign: 'center',
                            border: '2px dashed rgba(102, 126, 234, 0.2)'
                        }}>
                            <Box sx={{ opacity: 0.5, mb: 2 }}>
                                <PlayCircleOutlineIcon sx={{ fontSize: 64, color: '#667eea' }} />
                            </Box>
                            <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                                Votre cours est pour l'instant vide.
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                                Commencez par créer votre première section pour organiser votre contenu.
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenSectionModal(null)}
                                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
                            >
                                Créer une section
                            </Button>
                        </Paper>
                    )}
                </Box>

                {/* Modals */}
                <SectionModal
                    open={sectionModalOpen}
                    onClose={() => setSectionModalOpen(false)}
                    onSave={handleSaveSection}
                    initialData={editingSection || undefined}
                    isEditing={!!editingSection}
                />

                {/* Content Editor Dialog - Full Screen */}
                {contentEditorOpen && (
                    <Box sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'background.default',
                        zIndex: 1300,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box sx={{
                            p: 2,
                            borderBottom: '1px solid rgba(0,0,0,0.1)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: 'white',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                                <Avatar sx={{ bgcolor: 'rgba(102,126,234,0.1)', color: '#667eea', width: 40, height: 40 }}>
                                    <EditIcon fontSize="small" />
                                </Avatar>
                                <TextField
                                    variant="standard"
                                    value={lessonTitle}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLessonTitle(e.target.value)}
                                    placeholder="Saisissez le titre de la leçon..."
                                    InputProps={{
                                        disableUnderline: true,
                                        sx: {
                                            fontSize: '1.25rem',
                                            fontWeight: 800,
                                            fontFamily: 'Quicksand, sans-serif',
                                            color: '#2d3748'
                                        }
                                    }}
                                    sx={{ width: '450px' }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    onClick={() => setContentEditorOpen(false)}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        color: '#718096',
                                        fontFamily: 'Quicksand, sans-serif'
                                    }}
                                >
                                    Abandonner
                                </Button>
                                <Button
                                    onClick={() => setContentEditorOpen(false)}
                                    variant="outlined"
                                    color="error"
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontFamily: 'Quicksand, sans-serif',
                                        borderColor: 'rgba(245, 101, 101, 0.3)'
                                    }}
                                >
                                    Fermer l'éditeur
                                </Button>
                            </Box>
                        </Box>
                        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                            <ContentEditor
                                initialContent=""
                                initialBlocks={editingLesson?.blocks}
                                initialVideo={editingLesson?.video}
                                initialQuiz={editingLesson?.quiz}
                                initialType={editingLesson?.type || newLessonType || 'text'}
                                initialTitle={editingLesson?.title || ''}
                                onSave={handleSaveContent}
                                onCancel={() => setContentEditorOpen(false)}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </DndProvider>
    );
};

// Draggable Section Component
import { useDrag, useDrop } from 'react-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

interface DraggableSectionProps {
    section: Section;
    index: number;
    moveSection: (dragIndex: number, hoverIndex: number) => void;
    onDragEnd: () => void;
    onEdit: () => void;
    onEditLesson: (sectionId: string, lesson: Lesson) => void;
    onDeleteLesson: (lessonId: string) => void;
    onAddLesson: (sectionId: string, type: 'text' | 'video' | 'quiz' | 'exercise') => void;
    getIconForType: (type: string) => React.ReactNode;
}

const DraggableSection: React.FC<DraggableSectionProps> = ({
    section, index, moveSection, onDragEnd, onEdit,
    onEditLesson, onDeleteLesson, onAddLesson, getIconForType
}) => {
    const ref = React.useRef<HTMLDivElement>(null);

    const [{ handlerId }, drop] = useDrop({
        accept: 'SECTION',
        collect(monitor) {
            return { handlerId: monitor.getHandlerId() };
        },
        hover(item: any, monitor) {
            if (!ref.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;
            moveSection(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: 'SECTION',
        item: { id: section._id, index },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        end: () => onDragEnd()
    });

    drag(drop(ref));

    return (
        <Box ref={ref} sx={{ opacity: isDragging ? 0.5 : 1, mb: 3 }} data-handler-id={handlerId}>
            <Accordion
                defaultExpanded
                sx={{
                    ...designTokens.glassCard,
                    border: '1px solid rgba(102, 126, 234, 0.1) !important',
                    '&:before': { display: 'none' },
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    '&.Mui-expanded': { m: '0 0 24px 0', boxShadow: '0 10px 30px rgba(102, 126, 234, 0.15)' }
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#667eea' }} />}
                    sx={{
                        bgcolor: 'rgba(255,255,255,0.5)',
                        borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
                        '& .MuiAccordionSummary-content': { display: 'flex', alignItems: 'center' }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                        <Box sx={{ mr: 2, cursor: 'grab', display: 'flex', alignItems: 'center', color: '#667eea', opacity: 0.6 }}>
                            <DragIndicatorIcon />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif', fontSize: '1.1rem' }}>
                                {section.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                {section.lessons?.length || 0} Leçons
                            </Typography>
                        </Box>
                        <Tooltip title="Modifier le titre de la section">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }} sx={{ bgcolor: 'rgba(102,126,234,0.05)', color: '#667eea' }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <List disablePadding>
                        {section.lessons?.map((lesson) => (
                            <ListItem
                                key={lesson._id}
                                secondaryAction={
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Tooltip title="Éditer le contenu">
                                            <IconButton edge="end" onClick={() => onEditLesson(section._id, lesson)} sx={{ color: '#667eea', bgcolor: 'rgba(102,126,234,0.05)', '&:hover': { bgcolor: 'rgba(102,126,234,0.1)' } }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer la leçon">
                                            <IconButton edge="end" onClick={() => onDeleteLesson(lesson._id)} sx={{ color: '#f44336', bgcolor: 'rgba(244,67,54,0.05)', '&:hover': { bgcolor: 'rgba(244,67,54,0.1)' } }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                }
                                sx={{
                                    bgcolor: 'white',
                                    mb: 1.5,
                                    borderRadius: 3,
                                    p: 2,
                                    border: '1px solid rgba(0,0,0,0.02)',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateX(8px)',
                                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.1)',
                                        bgcolor: 'white'
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 48 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(0,0,0,0.03)', fontSize: '1rem' }}>
                                        {getIconForType(lesson.type)}
                                    </Avatar>
                                </ListItemIcon>
                                <ListItemText
                                    primary={lesson.title}
                                    secondary={lesson.type.charAt(0).toUpperCase() + lesson.type.slice(1)}
                                    primaryTypographyProps={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif', color: '#2c3e50' }}
                                    secondaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}
                                />
                            </ListItem>
                        ))}
                        {(!section.lessons || section.lessons.length === 0) && (
                            <Box sx={{ p: 4, textAlign: 'center', opacity: 0.6 }}>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    Aucune leçon dans cette section.
                                </Typography>
                            </Box>
                        )}
                    </List>

                    <Box sx={{ mt: 3, pt: 3, borderTop: '1px dashed rgba(102, 126, 234, 0.2)', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
                        <Typography variant="caption" sx={{ width: '100%', textAlign: 'center', fontWeight: 800, color: '#667eea', mb: -1, display: { xs: 'block', sm: 'none' } }}>
                            AJOUTER UNE LEÇON :
                        </Typography>
                        <Button
                            startIcon={<ArticleIcon />}
                            variant="outlined"
                            onClick={() => onAddLesson(section._id, 'text')}
                            sx={{ borderRadius: 3, textTransform: 'none', px: 3, fontWeight: 700, borderColor: 'rgba(102, 126, 234, 0.3)', color: '#667eea' }}
                        >
                            Texte / Blocs
                        </Button>
                        <Button
                            startIcon={<PlayCircleIcon />}
                            variant="outlined"
                            color="secondary"
                            onClick={() => onAddLesson(section._id, 'video')}
                            sx={{ borderRadius: 3, textTransform: 'none', px: 3, fontWeight: 700, borderColor: 'rgba(118, 75, 162, 0.3)', color: '#764ba2' }}
                        >
                            Vidéo
                        </Button>
                        <Button
                            startIcon={<QuizIcon />}
                            variant="outlined"
                            color="success"
                            onClick={() => onAddLesson(section._id, 'quiz')}
                            sx={{ borderRadius: 3, textTransform: 'none', px: 3, fontWeight: 700, borderColor: 'rgba(16, 185, 129, 0.3)', color: '#10b981' }}
                        >
                            Quiz
                        </Button>
                        <Button
                            startIcon={<SchoolIcon />}
                            variant="outlined"
                            sx={{ borderRadius: 3, textTransform: 'none', px: 3, fontWeight: 700, borderColor: 'rgba(255, 140, 0, 0.3)', color: '#ff8c00' }}
                            onClick={() => onAddLesson(section._id, 'exercise')}
                        >
                            Exercice
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default CourseDetails;
